import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import _uniqueId from 'lodash/uniqueId';
import React, {
  AriaRole,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ICONS } from '../../../consts/consts';

export interface SwitchInputProps {
  ariaLabel?: string;
  centered?: boolean;
  checkedValue?: boolean | string | object;
  className?: string;
  disabled?: boolean;
  enableHelp?: boolean;
  hoverHelperId?: string;
  hoverHelperText?: string;
  id?: string;
  name?: string;
  onChange?: (value: any) => void;
  required?: boolean;
  role?: AriaRole;
  style?: React.CSSProperties;
  switchLabel?: React.ReactNode;
  switchLabelClassName?: string;
  uncheckedValue?: boolean | string | object;
  value?: any;
}

const SwitchInput: FC<SwitchInputProps> = ({
  value = false,
  checkedValue = true,
  uncheckedValue = false,
  switchLabelClassName = '',
  ...props
}) => {
  const [switchValue, setSwitchValue] = useState(value === checkedValue);

  useEffect(() => {
    setSwitchValue(value === checkedValue);
  }, [checkedValue, value]);

  const propsOnChange = props.onChange;

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setSwitchValue(true);
      } else {
        setSwitchValue(false);
      }

      if (propsOnChange) {
        if (e.target.checked) {
          propsOnChange(checkedValue);
        } else {
          propsOnChange(uncheckedValue);
        }
      }
    },
    [checkedValue, uncheckedValue, propsOnChange]
  );

  // We either use the passed used hoverHelperId or generate one on the flight.
  const hoverHelperId = useMemo(
    () => 'helper-' + (props.hoverHelperId ?? _uniqueId()),
    [props.hoverHelperId]
  );

  // we need a unique id, either provided or generated,
  // for toggling to work properly
  const id = props.id
    ? props.id
    : props.name
      ? 'switch-' + props.name
      : _uniqueId('switch-');

  return (
    <div
      className={
        'form-check form-switch linebreak' +
        (props.switchLabel ? '' : ' me-n2') +
        (props.centered ? ' text-center' : '')
      }
    >
      <input
        aria-label={props.ariaLabel}
        id={id}
        type="checkbox"
        required={props.required}
        role={props.role}
        style={props.style}
        name={props.name}
        checked={switchValue}
        onChange={onChange}
        className={
          'form-check-input' + (props.className ? ' ' + props.className : '')
        }
        disabled={props.disabled}
      />
      <label
        className={`form-check-label ${switchLabelClassName}`}
        htmlFor={id}
        role="button"
        style={{
          lineHeight: '1.5rem',
        }}
      >
        {props.switchLabel}
      </label>
      {props.enableHelp && (
        <>
          <i
            id={hoverHelperId}
            className={'ms-2 ' + ICONS.NOT_ACKNOWLEDGED}
            style={{ position: 'relative', top: 1 }}
          />
          <UncontrolledPopover
            placement="top"
            trigger="hover"
            target={hoverHelperId}
            popperClassName={'linebreak'}
          >
            {props.hoverHelperText}
          </UncontrolledPopover>
        </>
      )}
    </div>
  );
};

export default React.memo(SwitchInput);
